import styled from "styled-components";
import {
  mobileAuthSideContainer,
  mobileAuthMainContainer,
} from "../../../../styles/authShellMixins";

export const MainContainer = styled.div`
  position: relative;
  display: flex;
  width: 100%;
  height: 100%;
  background: var(--app-bg-gradient);
  background-size: 400% 400%;
  font-family: "Inter", "Helvetica Neue", sans-serif;
  overflow-x: hidden;

  ${mobileAuthMainContainer}
`;

export const SideContainer = styled.div`
  scrollbar-width: none;
  overflow-y: auto;
  overflow-x: hidden;
  position: relative;
  height: 100vh;
  min-height: 100vh;
  width: ${({ collapsed }) =>
    collapsed ? "calc(100% - 6vw)" : "calc(100% - 15vw)"};
  margin: 0;
  transition: margin-left 0.5s ease-in-out, width 0.5s ease-in-out;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: flex-start;
  box-sizing: border-box;

  &::-webkit-scrollbar {
    display: none;
  }

  ${mobileAuthSideContainer}
`;

export const ContentWrapper = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 1100px;
  margin: 0 auto;
  padding: 1.25rem 1.5rem 2.5rem;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 0.75rem 0.85rem 2rem;
  }
`;

export const TopBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-start;
  gap: 0.75rem;
  width: 100%;
  margin-bottom: 0.5rem;
`;

export const BackButton = styled.button`
  background: var(--accent-soft);
  border: 1px solid var(--border-default);
  border-radius: 999px;
  color: var(--text-heading);
  cursor: pointer;
  font-family: inherit;
  padding: 0.55rem 1.1rem;
  font-size: 0.88rem;
  font-weight: 600;
  min-height: 40px;

  &:hover {
    border-color: var(--accent);
  }
`;

export const HeaderSection = styled.header`
  text-align: left;
  margin-bottom: 1rem;
  width: 100%;
`;

export const Title = styled.h1`
  font-size: clamp(1.5rem, 4vw, 2.1rem);
  font-weight: 700;
  background: linear-gradient(
    135deg,
    var(--accent-secondary) 0%,
    var(--accent) 100%
  );
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin: 0.35rem 0;
  text-align: left;
  line-height: 1.25;
`;

export const Subtitle = styled.p`
  margin: 0;
  color: var(--text-secondary);
  font-size: 0.95rem;
`;

export const SummaryRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 0.75rem;
  width: 100%;
  margin-bottom: 1rem;
`;

export const SummaryCard = styled.button`
  appearance: none;
  text-align: left;
  font: inherit;
  background: ${({ $active }) =>
    $active
      ? "color-mix(in srgb, var(--accent) 14%, var(--surface-1))"
      : "var(--surface-1)"};
  border: 1px solid
    ${({ $active }) => ($active ? "var(--accent)" : "var(--border-default)")};
  border-radius: 12px;
  padding: 0.85rem 1rem;
  cursor: ${({ $interactive }) => ($interactive ? "pointer" : "default")};
  transition: border-color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease;
  box-shadow: ${({ $active }) =>
    $active ? "0 0 0 1px color-mix(in srgb, var(--accent) 35%, transparent)" : "none"};

  &:disabled {
    opacity: 1;
    cursor: default;
  }

  ${({ $interactive }) =>
    $interactive
      ? `
    &:hover {
      border-color: var(--accent);
    }
    &:focus-visible {
      outline: 2px solid var(--accent);
      outline-offset: 2px;
    }
  `
      : ""}
`;

export const SummaryLabel = styled.div`
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.03em;
  margin-bottom: 0.25rem;
`;

export const SummaryValue = styled.div`
  font-size: 1.25rem;
  font-weight: 700;
  color: ${({ $tone }) =>
    $tone === "danger"
      ? "var(--danger)"
      : $tone === "ok"
        ? "var(--accent)"
        : "var(--text-heading)"};
`;

export const ToolbarRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  width: 100%;
  margin-bottom: 0.75rem;
`;

export const ResultCount = styled.p`
  margin: 0;
  color: var(--text-muted);
  font-size: 0.85rem;
`;

export const PerPageWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

export const PerPageLabel = styled.span`
  color: var(--text-secondary);
  font-size: 0.85rem;
`;

export const PerPageSelect = styled.select`
  appearance: none;
  padding: 0.45rem 2rem 0.45rem 0.75rem;
  border-radius: 12px;
  border: 1px solid var(--border-subtle);
  background-color: var(--input-bg);
  background-image:
    linear-gradient(45deg, transparent 50%, var(--text-heading) 50%),
    linear-gradient(135deg, var(--text-heading) 50%, transparent 50%);
  background-repeat: no-repeat;
  background-position:
    calc(100% - 12px) calc(50% - 2px),
    calc(100% - 7px) calc(50% - 2px);
  background-size: 5px 5px, 5px 5px;
  color: var(--text-primary);
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  outline: none;
  min-height: 36px;

  option {
    background: var(--surface-1);
    color: var(--text-primary);
  }

  &:focus {
    border-color: var(--accent);
  }
`;

export const TableWrap = styled.div`
  width: 100%;
  overflow-x: auto;
  background: var(--surface-1);
  border: 1px solid var(--border-default);
  border-radius: 14px;
  box-shadow: var(--shadow-sm);
`;

export const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.88rem;
  color: var(--text-primary);

  thead {
    background: var(--table-header-bg);
  }

  th {
    text-align: left;
    padding: 0.85rem 1rem;
    font-weight: var(--table-header-font-weight);
    color: var(--table-header-color);
    font-size: var(--table-header-font-size);
    text-transform: uppercase;
    letter-spacing: var(--table-header-letter-spacing);
    border-bottom: var(--table-header-border);
    white-space: nowrap;
  }

  td {
    padding: 0.8rem 1rem;
    border-bottom: 1px solid var(--border-subtle);
    vertical-align: top;
  }

  tbody tr:last-child td {
    border-bottom: none;
  }

  tbody tr:hover td {
    background: var(--surface-hover);
  }

  tbody tr[data-expanded="true"] td {
    background: color-mix(in srgb, var(--accent-soft) 55%, transparent);
  }
`;

export const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.2rem 0.55rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  white-space: nowrap;
  background: ${({ $tone }) =>
    $tone === "danger"
      ? "color-mix(in srgb, var(--danger) 18%, transparent)"
      : $tone === "warn"
        ? "color-mix(in srgb, var(--warning) 18%, transparent)"
        : "var(--accent-soft)"};
  color: ${({ $tone }) =>
    $tone === "danger"
      ? "var(--danger)"
      : $tone === "warn"
        ? "var(--warning)"
        : "var(--accent)"};
`;

export const Mono = styled.span`
  font-family: ui-monospace, monospace;
  font-size: 0.8rem;
`;

export const Muted = styled.span`
  color: var(--text-muted);
`;

export const ActionButton = styled.button`
  background: var(--surface-2);
  border: 1px solid var(--border-default);
  border-radius: 999px;
  color: var(--text-heading);
  cursor: pointer;
  font-family: inherit;
  padding: 0.4rem 0.8rem;
  font-size: 0.8rem;
  font-weight: 600;
  min-height: 34px;
  white-space: nowrap;

  &:hover:not(:disabled) {
    border-color: var(--accent);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const DetailRow = styled.tr`
  td {
    background: var(--surface-2) !important;
    padding: 0.75rem 1rem 1rem;
  }
`;

export const DetailPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
`;

export const DetailTitle = styled.p`
  margin: 0;
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--text-heading);
`;

export const DetailMeta = styled.p`
  margin: 0;
  font-size: 0.8rem;
  color: var(--text-secondary);
  line-height: 1.4;
`;

export const DetectionList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 0.45rem;
`;

export const DetectionItem = styled.li`
  font-size: 0.78rem;
  color: var(--text-primary);
  padding: 0.5rem 0.65rem;
  background: var(--surface-1);
  border: 1px solid var(--border-subtle);
  border-radius: 10px;
  line-height: 1.35;
`;

export const PaginationContainer = styled.div`
  margin-top: 1.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

export const PageButton = styled.button`
  background: transparent;
  border: 2px solid var(--border-subtle);
  color: var(--text-primary);
  border-radius: 50%;
  width: 40px;
  height: 40px;
  cursor: pointer;
  font-size: 0.95rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    border-color: var(--accent);
    color: var(--accent-secondary);
  }

  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }
`;

export const EmptyState = styled.div`
  color: var(--text-secondary);
  font-size: 0.95rem;
  padding: 1.25rem;
  text-align: left;
  background: var(--surface-1);
  border: 1px solid var(--border-default);
  border-radius: 12px;
  margin: 0;
`;

export const LoadingText = styled.p`
  color: var(--text-secondary);
  padding: 1rem 0;
  margin: 0;
`;

export const DesktopOnly = styled.div`
  width: 100%;

  @media (max-width: 768px) {
    display: none;
  }
`;

export const MobileCards = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    width: 100%;
  }
`;

export const MobileCard = styled.article`
  background: var(--surface-1);
  border: 1px solid var(--border-default);
  border-radius: 14px;
  padding: 0.9rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
`;

export const MobileCardTitle = styled.h3`
  margin: 0;
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--text-heading);
`;

export const MobileCardMeta = styled.p`
  margin: 0;
  font-size: 0.8rem;
  color: var(--text-secondary);
  line-height: 1.4;
`;

export const MobileCardActions = styled.div`
  margin-top: 0.4rem;
`;
